// AgentMaurice chart palette (AGENT-731 OSA-08, AGENT-832). The constant is
// exported by @agent-maurice/ui (`chartPalette`, generated there from
// `--chart-1..6` of `styles/tokens.css` and checked by its `check:charts`).
// Fixed order (visual-identity, palette des graphiques): series i always
// takes slot i. readChartPalette below is kept for the cross-check: the tests
// compare the package constant with the tokens the OS actually loads.
import { chartPalette } from '@agent-maurice/ui/chart-palette'

export type ChartPalette = { light: string[]; dark: string[] }

const TRIPLET = /^(-?\d+(?:\.\d+)?)\s+(\d+(?:\.\d+)?)%\s+(\d+(?:\.\d+)?)%$/

function hslTripletToHex(triplet: string): string | null {
  const m = triplet.trim().match(TRIPLET)
  if (!m) return null
  const h = Number(m[1])
  const s = Number(m[2]) / 100
  const l = Number(m[3]) / 100
  const k = (n: number) => (n + h / 30) % 12
  const a = s * Math.min(l, 1 - l)
  const f = (n: number) => l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)))
  return `#${[f(0), f(8), f(4)]
    .map((c) => Math.round(c * 255).toString(16).padStart(2, '0').toUpperCase())
    .join('')}`
}

// readChartPalette parses the `:root` (light) and `.dark` (night) blocks of a
// token style sheet and returns `--chart-1..6` as hex colours, in order.
export function readChartPalette(css: string): ChartPalette {
  const clean = css.replace(/\/\*[\s\S]*?\*\//g, '')
  const values: Record<'light' | 'dark', Record<string, string>> = { light: {}, dark: {} }
  for (const block of clean.matchAll(/^(:root|\.dark)\s*\{([^}]*)\}/gm)) {
    const target = block[1] === ':root' ? values.light : values.dark
    for (const decl of block[2].matchAll(/(--chart-\d)\s*:\s*([^;]+);/g)) target[decl[1]] = decl[2]
  }
  const series = (mode: 'light' | 'dark') =>
    Array.from({ length: 6 }, (_, i) => {
      const name = `--chart-${i + 1}`
      const hex = hslTripletToHex(values[mode][name] ?? values.light[name] ?? '')
      if (!hex) throw new Error(`@agent-maurice/ui tokens: ${name} missing or not an HSL triplet (${mode})`)
      return hex
    })
  return { light: series('light'), dark: series('dark') }
}

// Mutable copies: OpenUI's defaultChartPalette expects string[].
export const agentMauriceChartPalette: ChartPalette = {
  light: [...chartPalette.light],
  dark: [...chartPalette.dark],
}
